<template>
  <div class="calendar-view-content">
    <!-- Calendar Header -->
    <div class="calendar-header-section mb-4">
      <!-- Title Section -->
      <div class="text-center mb-4">
        <h2 class="calendar-title">Event Calendar</h2>
        <p class="calendar-subtitle">{{ currentTabTitle }}</p>
      </div>

      <!-- Tab Switch & Controls Row -->
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-3">
        <!-- Tab Toggle -->
        <div class="tab-toggle-group">
          <button
            class="tab-toggle"
            :class="{ active: currentTab === 'all' }"
            @click="currentTab = 'all'"
          >
            <i class="bi bi-calendar-event me-2"></i>All Events
          </button>
          <button
            class="tab-toggle"
            :class="{ active: currentTab === 'my' }"
            @click="currentTab = 'my'"
          >
            <i class="bi bi-calendar-check me-2"></i>My Bookings
          </button>
        </div>

        <!-- View Mode Toggle -->
        <div class="view-mode-toggle">
          <button
            class="view-mode-btn"
            :class="{ active: currentView === 'dayGridMonth' }"
            @click="changeView('dayGridMonth')"
          >
            <i class="bi bi-calendar-month"></i> Month
          </button>
          <button
            class="view-mode-btn"
            :class="{ active: currentView === 'timeGridWeek' }"
            @click="changeView('timeGridWeek')"
          >
            <i class="bi bi-calendar-week"></i> Week
          </button>
        </div>

        <!-- Event Count -->
        <div class="event-count">
          <i class="bi bi-calendar-event me-1"></i>
          <span class="count-number">{{ currentTab === 'all' ? events.length : (bookings.length + registrationEvents.length) }}</span>
          <span class="count-text">events</span>
        </div>
      </div>

      <!-- Legend -->
      <div class="calendar-legend">
        <template v-if="currentTab === 'all'">
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #FF7BAC"></span>
            <span class="legend-label">Fitness Class</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #C38FFF"></span>
            <span class="legend-label">Health Talk</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #6EC1E4"></span>
            <span class="legend-label">Outdoor Hiking</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #8FD694"></span>
            <span class="legend-label">Wellness Salon</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #B0B0B0"></span>
            <span class="legend-label">Other</span>
          </div>
        </template>
        <template v-else>
          <div class="legend-item">
            <span class="legend-dot star-dot" style="background-color: #FFB74D">★</span>
            <span class="legend-label">My Bookings</span>
          </div>
        </template>
      </div>
    </div>

    <!-- FullCalendar -->
    <div class="calendar-wrapper">
      <FullCalendar v-if="!loading" :options="calendarOptions" ref="fullCalendar" />
      <div v-else class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="showEmptyState" class="empty-state-overlay">
      <div class="empty-state-content">
        <i :class="emptyStateIcon" class="display-3 text-muted"></i>
        <h5 class="mt-3">{{ emptyStateTitle }}</h5>
        <p class="text-muted">{{ emptyStateMessage }}</p>
        <button v-if="currentTab === 'my'" class="btn btn-primary" @click="currentTab = 'all'">
          <i class="bi bi-calendar-event"></i> Browse All Events
        </button>
      </div>
    </div>

    <!-- Event Preview Modal -->
    <EventPreviewModal
      v-if="showPreviewModal"
      :event-id="selectedEventId"
      :is-my-booking="currentTab === 'my'"
      :booking-details="selectedBooking"
      @close="closePreviewModal"
      @cancel-booking="handleCancelBooking"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { useToast } from '@/composables/useToast'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { getUserBookings, cancelBooking } from '@/services/bookingService'
import { getUserRegistrations, getEvent } from '@/services/eventService'
import EventPreviewModal from './EventPreviewModal.vue'

export default {
  name: 'CalendarViewContent',
  components: {
    FullCalendar,
    EventPreviewModal
  },
  props: {
    events: {
      type: Array,
      default: () => []
    },
    selectedCategory: {
      type: String,
      default: 'all'
    }
  },
  setup(props) {
    const router = useRouter()
    const { user, isAuthenticated } = useFirebaseAuth()
    const toast = useToast()

    const loading = ref(false)
    const fullCalendar = ref(null)
    const currentView = ref('dayGridMonth')
    const currentTab = ref('all')
    const bookings = ref([])
    const registrations = ref([])
    const registrationEvents = ref([])

    // Modal
    const showPreviewModal = ref(false)
    const selectedEventId = ref(null)
    const selectedBooking = ref(null)

    const categoryColors = {
      'Fitness Class': '#FF7BAC',
      'Health Talk': '#C38FFF',
      'Outdoor Hiking': '#6EC1E4',
      'Wellness Salon': '#8FD694',
      Other: '#B0B0B0',
      default: '#B0B0B0'
    }

    const currentTabTitle = computed(() => {
      return currentTab.value === 'all'
        ? 'View and manage all events'
        : 'View your bookings'
    })

    const showEmptyState = computed(() => {
      if (currentTab.value === 'all') {
        return props.events.length === 0 && !loading.value
      } else {
        return bookings.value.length === 0 && registrationEvents.value.length === 0 && !loading.value
      }
    })

    const emptyStateIcon = computed(() => {
      return currentTab.value === 'all' ? 'bi bi-calendar-x' : 'bi bi-calendar-event'
    })

    const emptyStateTitle = computed(() => {
      return currentTab.value === 'all' ? 'No events found' : "You don't have any bookings yet"
    })

    const emptyStateMessage = computed(() => {
      return currentTab.value === 'all'
        ? 'Try another category or check back later.'
        : 'Browse all events to make your first booking.'
    })

    const calendarEvents = computed(() => {
      if (currentTab.value === 'my') {
        const allMyEvents = []

        // Add bookings
        bookings.value.forEach((booking) => {
          allMyEvents.push({
            id: `booking-${booking.id}`,
            title: booking.event_title || 'My Booking',
            start: booking.start_time,
            end: booking.end_time,
            backgroundColor: '#FFB74D',
            borderColor: '#FF9800',
            extendedProps: {
              type: 'booking',
              bookingId: booking.id,
              bookingData: booking
            }
          })
        })

        // Add registrations
        registrationEvents.value.forEach((event) => {
          const startTime = event.time_start || event.event_date
          const endTime = event.time_end || event.end_date
          const start = startTime?.toDate ? startTime.toDate() : new Date(startTime)
          const end = endTime?.toDate
            ? endTime.toDate()
            : new Date(start.getTime() + 2 * 60 * 60 * 1000)

          allMyEvents.push({
            id: `registration-${event.id}`,
            title: event.title,
            start,
            end,
            backgroundColor: '#FFB74D',
            borderColor: '#FF9800',
            extendedProps: {
              type: 'registration',
              eventId: event.id,
              category: event.category,
              location: event.location,
              max_participants: event.max_participants || event.capacity,
              current_participants: event.current_participants || event.participants || 0,
              description: event.description,
              eventData: event
            }
          })
        })

        return allMyEvents
      } else {
        let filteredEvents = props.events

        if (props.selectedCategory !== 'all') {
          filteredEvents = filteredEvents.filter((e) => e.category === props.selectedCategory)
        }

        return filteredEvents.map((event) => {
          // Handle time fields - support time_start/time_end or event_date/end_date
          const startTime = event.time_start || event.event_date
          const endTime = event.time_end || event.end_date

          const start = startTime?.toDate ? startTime.toDate() : new Date(startTime)
          const end = endTime?.toDate
            ? endTime.toDate()
            : new Date(start.getTime() + 2 * 60 * 60 * 1000) // Default 2 hours

          return {
            id: event.id,
            title: event.title,
            start,
            end,
            color: categoryColors[event.category] || categoryColors.default,
            extendedProps: {
              type: 'event',
              category: event.category,
              location: event.location,
              max_participants: event.max_participants || event.capacity,
              current_participants: event.current_participants || event.participants || 0,
              description: event.description,
              eventData: event
            }
          }
        })
      }
    })

    const calendarOptions = computed(() => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: ''
      },
      height: 'auto',
      selectable: false,
      editable: false,
      events: calendarEvents.value,
      eventClick: handleEventClick,
      eventDidMount: handleEventRender,
      eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }
    }))

    const loadBookings = async () => {
      if (!isAuthenticated.value || !user.value) {
        return
      }

      loading.value = true
      try {
        // Load bookings (calendar system)
        bookings.value = await getUserBookings(user.value.uid)

        // Load registrations (activity sign-ups)
        registrations.value = await getUserRegistrations(user.value.uid)

        // Fetch event details for each registration
        const eventPromises = registrations.value.map((reg) => getEvent(reg.event_id))
        const eventsList = await Promise.all(eventPromises)

        // Filter out null values (deleted events)
        registrationEvents.value = eventsList.filter((e) => e !== null)
      } catch (error) {
        console.error('Error loading bookings:', error)
        toast.error('Failed to load bookings')
      } finally {
        loading.value = false
      }
    }

    const changeView = (viewName) => {
      currentView.value = viewName
      const calendarApi = fullCalendar.value?.getApi()
      if (calendarApi) {
        calendarApi.changeView(viewName)
      }
    }

    const handleEventClick = (info) => {
      const event = info.event

      if (event.extendedProps.type === 'booking') {
        selectedEventId.value = event.extendedProps.bookingData.event_id || ''
        selectedBooking.value = event.extendedProps.bookingData
      } else if (event.extendedProps.type === 'registration') {
        selectedEventId.value = event.extendedProps.eventId
        selectedBooking.value = null
      } else {
        selectedEventId.value = event.id
        selectedBooking.value = null
      }

      showPreviewModal.value = true
    }

    const handleEventRender = (info) => {
      const event = info.event

      if (event.extendedProps.type === 'event') {
        const props = event.extendedProps
        const isFull = props.current_participants >= props.max_participants

        info.el.title = `${event.title}\n${event.start.toLocaleString()}\nLocation: ${
          props.location || 'TBA'
        }\nParticipants: ${props.current_participants}/${props.max_participants}${
          isFull ? ' (FULL)' : ''
        }`

        if (isFull) {
          const fullBadge = document.createElement('span')
          fullBadge.className = 'badge bg-danger ms-1'
          fullBadge.textContent = 'Full'
          fullBadge.style.fontSize = '0.7em'
          info.el.querySelector('.fc-event-title')?.appendChild(fullBadge)
        }
      } else if (event.extendedProps.type === 'registration') {
        const props = event.extendedProps
        info.el.title = `${event.title} (Registered)\n${event.start.toLocaleString()}\nLocation: ${
          props.location || 'TBA'
        }\nParticipants: ${props.current_participants}/${props.max_participants}`
      } else {
        const booking = event.extendedProps.bookingData
        info.el.title = `My Booking\n${event.start.toLocaleString()}\nNotes: ${
          booking.notes || 'None'
        }`
      }
    }

    const handleCancelBooking = async (bookingId) => {
      loading.value = true
      try {
        await cancelBooking(bookingId)
        bookings.value = bookings.value.filter((b) => b.id !== bookingId)
        toast.success('Booking cancelled successfully')
        closePreviewModal()
      } catch (error) {
        console.error('Error cancelling booking:', error)
        toast.error('Failed to cancel booking')
      } finally {
        loading.value = false
      }
    }

    const closePreviewModal = () => {
      showPreviewModal.value = false
      selectedEventId.value = null
      selectedBooking.value = null
    }

    watch(
      () => currentTab.value,
      (newTab) => {
        if (newTab === 'my') {
          loadBookings()
        }
      }
    )

    watch(isAuthenticated, (authenticated) => {
      if (!authenticated && currentTab.value === 'my') {
        currentTab.value = 'all'
      }
    })

    onMounted(() => {
      if (currentTab.value === 'my') {
        loadBookings()
      }
    })

    return {
      loading,
      fullCalendar,
      currentView,
      currentTab,
      calendarOptions,
      currentTabTitle,
      showEmptyState,
      emptyStateIcon,
      emptyStateTitle,
      emptyStateMessage,
      showPreviewModal,
      selectedEventId,
      selectedBooking,
      bookings,
      registrationEvents,
      changeView,
      closePreviewModal,
      handleCancelBooking
    }
  }
}
</script>

<style scoped>
.calendar-view-content {
  position: relative;
  min-height: 500px;
  font-family: 'Inter', 'Noto Sans', sans-serif;
}

/* ========== Header Section ========== */
.calendar-header-section {
  background: linear-gradient(135deg, #FFF5FA 0%, #FFFFFF 100%);
  padding: 2rem 1.5rem;
  border-radius: 14px;
  box-shadow: 0 3px 12px rgba(255, 79, 145, 0.08);
}

.calendar-title {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.calendar-subtitle {
  color: #666;
  font-size: 15px;
  margin-bottom: 0;
}

/* ========== Tab Toggle ========== */
.tab-toggle-group {
  display: flex;
  gap: 0.5rem;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 10px;
}

.tab-toggle {
  border-radius: 8px;
  padding: 8px 16px;
  border: none;
  color: #666;
  background-color: transparent;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.tab-toggle:hover {
  background: rgba(255, 79, 145, 0.1);
  color: #FF4F91;
}

.tab-toggle.active {
  background: linear-gradient(90deg, #FF4F91, #FF6FB4);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(255, 79, 145, 0.3);
}

/* ========== View Mode Toggle ========== */
.view-mode-toggle {
  display: flex;
  gap: 0.5rem;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 10px;
}

.view-mode-btn {
  border-radius: 8px;
  padding: 8px 14px;
  border: none;
  color: #666;
  background-color: transparent;
  font-weight: 500;
  font-size: 13px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.view-mode-btn:hover {
  background: rgba(255, 79, 145, 0.1);
  color: #FF4F91;
}

.view-mode-btn.active {
  background: linear-gradient(90deg, #FF4F91, #FF6FB4);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(255, 79, 145, 0.3);
}

/* ========== Event Count ========== */
.event-count {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #666;
  font-size: 14px;
  padding: 8px 12px;
  background: rgba(255, 79, 145, 0.05);
  border-radius: 8px;
}

.count-number {
  font-weight: 700;
  color: #FF4F91;
  font-size: 16px;
}

.count-text {
  color: #666;
  font-size: 13px;
}

/* ========== Legend ========== */
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  justify-content: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 79, 145, 0.1);
  margin-top: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  color: #666;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.star-dot {
  width: 16px;
  height: 16px;
  border-radius: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
}

.legend-label {
  font-weight: 500;
}

/* ========== Calendar Wrapper ========== */
.calendar-wrapper {
  background: linear-gradient(135deg, #FFF5FA 0%, #FFFFFF 100%);
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.05);
  position: relative;
  min-height: 600px;
}

.empty-state-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10;
}

.empty-state-content {
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* ========== FullCalendar Customization ========== */
:deep(.fc) {
  font-family: 'Inter', 'Noto Sans', sans-serif;
}

:deep(.fc-toolbar-title) {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

:deep(.fc-button) {
  background: linear-gradient(90deg, #FF4F91, #FF6FB4);
  border: none;
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

:deep(.fc-button:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(255, 79, 145, 0.3);
}

:deep(.fc-button:disabled) {
  background: #e0e0e0;
  opacity: 0.6;
}

/* ========== Day Cells ========== */
:deep(.fc-daygrid-day) {
  transition: all 0.2s ease;
}

:deep(.fc-daygrid-day:hover) {
  background: #FFF7FA;
  transform: scale(1.01);
}

:deep(.fc-day-today) {
  background-color: #FFE4ED !important;
}

:deep(.fc-day-today .fc-daygrid-day-number) {
  background: linear-gradient(135deg, #FF4F91, #FF6FB4);
  color: white !important;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(255, 79, 145, 0.3);
}

:deep(.fc-daygrid-day-number) {
  padding: 4px;
  font-weight: 600;
  color: #333;
}

/* ========== Events ========== */
:deep(.fc-event) {
  cursor: pointer;
  background-color: transparent !important;
  border: none !important;
  padding: 3px 6px !important;
  border-radius: 6px !important;
  margin: 2px 0 !important;
  transition: all 0.2s ease !important;
}

:deep(.fc-event:hover) {
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

:deep(.fc-event .fc-event-main) {
  color: #333 !important;
}

:deep(.fc-daygrid-event-dot) {
  border-width: 5px !important;
  width: 10px !important;
  height: 10px !important;
  margin-right: 6px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

:deep(.fc-event-title) {
  color: #333 !important;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.3;
}

:deep(.fc-event-time) {
  color: #666 !important;
  font-size: 12px;
  font-weight: 500;
}

/* ========== Grid Lines ========== */
:deep(.fc-theme-standard td),
:deep(.fc-theme-standard th) {
  border-color: rgba(255, 79, 145, 0.1);
}

:deep(.fc-scrollgrid) {
  border-radius: 10px;
  overflow: hidden;
}

/* ========== Header ========== */
:deep(.fc-col-header-cell) {
  background: linear-gradient(135deg, #FFF5FA 0%, #FFE4ED 100%);
  font-weight: 700;
  color: #FF4F91;
  padding: 12px 8px;
}

:deep(.fc-col-header-cell-cushion) {
  color: #FF4F91;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
}

/* ========== Responsive Design ========== */
@media (max-width: 768px) {
  .calendar-header-section {
    padding: 1.5rem 1rem;
  }

  .calendar-title {
    font-size: 20px;
  }

  .calendar-subtitle {
    font-size: 14px;
  }

  .tab-toggle-group,
  .view-mode-toggle {
    width: 100%;
  }

  .tab-toggle,
  .view-mode-btn {
    flex: 1;
    font-size: 12px;
    padding: 6px 10px;
  }

  .event-count {
    display: none;
  }

  .calendar-legend {
    gap: 0.8rem;
    flex-direction: column;
    align-items: flex-start;
  }

  .calendar-wrapper {
    padding: 1rem;
  }

  :deep(.fc-toolbar-title) {
    font-size: 1.2rem;
  }

  :deep(.fc-event-title) {
    font-size: 11px;
  }

  :deep(.fc-col-header-cell-cushion) {
    font-size: 10px;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .calendar-legend {
    gap: 1rem;
  }

  .legend-label {
    font-size: 12px;
  }
}

@media (min-width: 1201px) {
  .d-flex.justify-content-between {
    flex-wrap: nowrap;
  }
}

/* ========== Animations ========== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.calendar-view-content {
  animation: fadeIn 0.3s ease-in-out;
}

:deep(.fc-event) {
  animation: fadeIn 0.2s ease-in-out;
}

/* ========== Smooth Transitions ========== */
* {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

button {
  transition: all 0.2s ease;
}

button:active {
  transform: scale(0.98);
}
</style>
